import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import { AppRouteBody } from '@/components/AppRouteBody';
import { ConditionalRender } from '@/components/ConditionalRender';
import {
  createAssetExportConfig,
  SourceOption,
} from '@/components/export/configs/asset.export.config';
import {
  buildActiveFilters,
  UniversalExportModal,
} from '@/components/export/universal';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Loader } from '@/components/Loader';
import { NoteModal } from '@/components/modals';
import { GetStartedModal } from '@/components/modals/GetStartedModal';
import { BulkConfirmationDialog } from '@/components/table/BulkConfirmationDialog';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { useScopedAssetFilters } from '@/hooks/api/useGetAssetFilters';
import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import type { Filters } from '@/hooks/useAssets';
import { useQueryAssets } from '@/hooks/useAssets';
import { useBulkAction } from '@/hooks/useBulkAction';
import { useBulkConfirmation } from '@/hooks/useBulkConfirmation/useBulkConfirmation';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useDefaultStatusFilter } from '@/hooks/useDefaultStatusFilter';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useFilteredCount } from '@/hooks/useFilteredCount';
import { useMy } from '@/hooks/useMy';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { AssetTable, AssetTableFilters } from '@/sections/asset/components';
import { AssetActions } from '@/sections/asset/components/actions';
import {
  RemoveTagModal,
  StatusConfirmationModal,
  TagModal,
} from '@/sections/asset/components/modals';
import { ImportAssetsModal } from '@/sections/asset/components/modals/ImportAssetsModal';
import { deriveColumnOptions } from '@/sections/asset/config/deriveColumnOptions';
import {
  ASSET_GET_STARTED_METHODS,
  type AssetGetStartedMethodId,
} from '@/sections/asset/config/getStartedMethods';
import {
  useAssetActions,
  useAssetColumns,
  useAssetModalState,
} from '@/sections/asset/hooks';
import {
  applyUrlParamsToFilters,
  isQueryError as checkIsQueryError,
  resetQueryParams as resetFilters,
} from '@/sections/asset/utils';
import { AddSeedByTypeModal } from '@/sections/seeds/components/modals/AddSeedByTypeModal';
import { BulkActivityAnnouncer } from '@/sections/topNavBar/BulkJobsDrawer/BulkActivityAnnouncer';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import type { GraphQuery } from '@/types';
import { AssetStatus } from '@/types';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { omit } from '@/utils/lodash.util';
import { buildSourceOptions } from '@/utils/sourceOptions.util';
import { getCurrentTenantId } from '@/utils/storage/tenantScoping.util';

/**
 * Component props for Assets
 */
export interface AssetsProps {
  /** Table storage key for column preferences */
  name?: string;
  /** Filter assets by vulnerability or other parent constraints */
  parentFilters?: { riskKey?: string } & Partial<Filters>;
  /** Hide AppRouteBody wrapper (drawer doesn't need it) */
  hideTitle?: boolean;
  /** Enable drawer-specific optimizations */
  inDrawer?: boolean;
  /** Limit visible filters in drawer mode: ['status', 'tag', 'attackSurface'] */
  drawerFilters?: string[];
  /** Hide bulk actions if needed */
  hideBulkActions?: boolean;
  /** Column ids to drop from the default-active set for this instance and route
   *  to the picker's available list instead. They remain selectable. Drawer use. */
  excludeDefaultColumns?: string[];
}

const applyAssetActiveFilter = (prev: Filters): Filters => ({
  ...prev,
  advancedFilters: {
    ...prev.advancedFilters,
    status: [AssetStatus.Active],
  },
});

const clearAssetActiveFilter = (prev: Filters): Filters => {
  const restAdvanced = omit(prev.advancedFilters, 'status');
  return {
    ...prev,
    advancedFilters:
      Object.keys(restAdvanced).length > 0 ? restAdvanced : undefined,
  };
};

/**
 * Maps AssetStatus to a human-readable bulk action name for the confirmation dialog.
 * Defined outside the component to avoid recreation on every render.
 */
function getBulkActionName(status: AssetStatus): string {
  switch (status) {
    case AssetStatus.Frozen:
      return 'freeze';
    case AssetStatus.Deleted:
      return 'delete';
    case AssetStatus.Active:
      return 'unfreeze';
    default:
      return 'update';
  }
}

export function Assets({
  name = 'assets',
  parentFilters,
  hideTitle = false,
  inDrawer = false,
  drawerFilters,
  hideBulkActions = false,
  excludeDefaultColumns,
}: AssetsProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { drawerState } = useGlobalState();
  const { canWriteAssets } = useEntitlements();

  const {
    data: assets,
    status: assetsStatus,
    error: assetsError,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    filters,
    setFilters,
    hasNextPage: assetsHasNextPage,
    invalidate: invalidateAssets,
    activeQuery,
    isFilteredEmpty: assetsIsFilteredEmpty,
  } = useQueryAssets({ parentFilters });

  // Apply URL parameters to filters on initial load
  // Skip in drawer context — drawer filters are ephemeral and should not
  // read from or write to URL parameters
  useEffect(() => {
    if (inDrawer) return;
    applyUrlParamsToFilters(searchParams, setFilters, () => {
      // Only clear filter-related params, preserve drawer params
      setSearchParams(prevParams => {
        const newParams = new URLSearchParams(prevParams);
        // Remove only filter params, keep drawer keys
        newParams.delete('attackSurface');
        newParams.delete('source');
        newParams.delete('search');
        newParams.delete('profile');
        return newParams;
      });
    });
  }, [searchParams, setFilters, setSearchParams, inDrawer]);

  // Check if error is due to query parameters (like orderBy issues)
  const isQueryError = checkIsQueryError(assetsError);

  // Reset function to clear problematic query parameters
  const resetQueryParams = () => resetFilters(setFilters);

  // Row selection management — uses useBulkSelection for Gmail-style two-step selection
  // activeQuery may be undefined while the query is still loading; we pass it as undefined
  // rather than casting an empty object as GraphQuery. The banner's enableSelectAllMatching
  // is disabled when activeQuery is undefined, preventing promotion to 'all' mode with a
  // stale/empty filter.
  const bulkSelection = useBulkSelection({
    data: assets,
    activeFilters: (activeQuery ?? undefined) as GraphQuery,
    filterVersion: filters,
  });

  const {
    selectedRows: selectedKeys,
    setSelectedRows: setSelectedKeys,
    hasSelection: isRowsSelected,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount,
    state: selectionState,
  } = bulkSelection;

  // Derive selectedAssets from selectedRows for action handlers
  const selectedAssets = assets.filter(a => selectedKeys.includes(a.key));

  // Fetch total asset count from database via statistics
  // Optimization: Skip when hideTitle is true (drawer context)
  // Total count is only displayed in the title, which is hidden in drawers
  const { data: assetStatistics } = useMy(
    {
      resource: 'statistic',
      query: '#class##asset##now#',
    },
    {
      staleTime: 60000, // Cache for 60 seconds
      refetchInterval: false, // Override default 30s polling
      enabled: !hideTitle, // Only fetch when title is shown
    }
  );

  // Compute total count from statistics response
  const totalDatabaseCount = useMemo(() => {
    if (!assetStatistics) return 0;
    return assetStatistics.reduce(
      (sum, item) => sum + (item.data?.count || 0),
      0
    );
  }, [assetStatistics]);

  // Fetch filtered count using the same query as the main data.
  // When no filters are applied, fall back to the base :Asset query so the
  // count and the table agree on which set is being counted. The table always
  // queries :Asset (the :CanonicalAsset label was retired in #5816/#5911).
  const countQuery = useMemo(
    () =>
      activeQuery ?? {
        node: {
          labels: ['Asset'],
        },
      },
    [activeQuery]
  );
  const { count: filteredCount, isLoading: isFilteredCountLoading } =
    useFilteredCount(
      {
        resource: 'asset',
        query: countQuery,
      },
      { enabled: !hideTitle }
    );

  // Compute isEmpty flags after totalDatabaseCount is available
  // Check both statistics count AND actual data — statistics can lag or
  // return 0 while the graph query still returns assets.
  const isAllEmpty =
    assetsStatus === 'success' &&
    !hideTitle &&
    totalDatabaseCount === 0 &&
    assets.length === 0;
  const isFilteredEmpty =
    assetsStatus === 'success' && assets.length === 0 && !isAllEmpty;

  // Apply default "Active" status filter on new sessions (main table only)
  useDefaultStatusFilter<Filters>({
    sessionKey: `${getCurrentTenantId()}:asset-default-active-filter`,
    applyFilter: applyAssetActiveFilter,
    clearFilter: clearAssetActiveFilter,
    setFilters,
    filters,
    isFilteredEmpty: assetsIsFilteredEmpty,
    dataStatus: assetsStatus,
    isFetching,
    enabled: !inDrawer && !parentFilters,
  });

  // Pre-fetch filter options at the parent level so the spinner waits for
  // BOTH the main asset data AND filter dropdown options to be ready.
  // Shares TanStack Query's cache with AssetTableFilters' internal call to
  // useScopedAssetFilters('asset-table') — no duplicate network requests.
  const { status: filterOptionsStatus } = useScopedAssetFilters('asset-table');

  // Filter options for export modal pill labels (cached — no extra API call)
  const {
    filterOptions: assetFilterOptions,
    sortedOptions: assetSortedOptions,
  } = useEntityFilters('Asset', 'asset-table');
  const assetFieldLabels = useMemo(
    () => Object.fromEntries(assetSortedOptions.map(o => [o.value, o.label])),
    [assetSortedOptions]
  );

  // Detect initial loading state to prevent table UI flicker
  // During pending state, we don't know if account is empty or has data
  const isInitialLoading =
    assetsStatus === 'pending' || filterOptionsStatus === 'pending';

  // Force table remount when transitioning from empty to having data
  const tableKey = useTableRemountOnTransition(isFilteredEmpty, assets.length);

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // NOTE: search is excluded - it's debounced at API level and shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        filters: filters.advancedFilters,
        sort: filters.sort,
      }),
    [filters.advancedFilters, filters.sort]
  );

  // Combine with existing tableKey for empty→data transitions
  const combinedTableKey = `${tableKey}-${filterKey}`;

  // Modal state management
  const {
    isCommentModalOpen,
    setIsCommentModalOpen,
    isTagModalOpen,
    setIsTagModalOpen,
    isRemoveTagModalOpen,
    setIsRemoveTagModalOpen,
    isImportModalOpen,
    setIsImportModalOpen,
    isExportModalOpen,
    setIsExportModalOpen,
    isGetStartedModalOpen,
    setIsGetStartedModalOpen,
    isAddSeedModalOpen,
    setIsAddSeedModalOpen,
    confirmStatus,
    setConfirmStatus,
  } = useAssetModalState();

  // User permissions for freeze/unfreeze warnings
  const { isPraetorianUser } = useAuth();

  // Bulk confirmation hook for the status_change bulk endpoint.
  // Encapsulates: dialog state + useBulkAction mutation + clearSelection wiring.
  // NOTE: does NOT call queryClient.invalidateQueries — that is handled by
  // useActiveBulkJobs (polls job status and invalidates on terminal state).
  const statusConfirm = useBulkConfirmation({
    resource: 'asset',
    action: 'status_change',
    bulkSelection,
    clearSelection,
  });

  // Bulk confirmation hook for the untag bulk endpoint.
  // Same shape as statusConfirm — uses resource:'asset', action:'untag'.
  // Wire format locked: action='untag', params:{tag}.
  const untagConfirm = useBulkConfirmation({
    resource: 'asset',
    action: 'untag',
    bulkSelection,
    clearSelection,
  });

  // Bulk confirmation hook for the remove_profile bulk endpoint.
  // Replaces the runChunked anti-pattern in AssetActions.handleRemoveProfile.
  // Per-item PUT /asset {profile: ''} is broken (MergeFields skips empty Profile).
  // Bulk endpoint uses direct graph mutation that bypasses the merge.
  const removeProfileConfirm = useBulkConfirmation({
    resource: 'asset',
    action: 'remove_profile',
    bulkSelection,
    clearSelection,
  });

  // Single-row remove (no confirmation dialog — tag-style "instant" UX).
  // Multi-asset path uses removeProfileConfirm above (with BulkConfirmationDialog).
  const removeProfileRowMutation = useBulkAction({
    resource: 'asset',
    action: 'remove_profile',
  });

  // Action handlers
  const { handlers, handleComment, handleKeyboardAction } = useAssetActions({
    invalidateAssets,
    selectedAssets,
    selectedRowsCount: selectedCount,
    setConfirmStatus,
    selectionState,
    onBulkRequested: (status: AssetStatus) => {
      statusConfirm.openConfirmation({
        action: 'status_change',
        actionName: getBulkActionName(status),
        params: { status },
      });
    },
  });

  // Column configuration. When excludeDefaultColumns is set (drawer use), the
  // table gets its own storage key + a reduced default-active/immutable set so
  // the excluded columns drop out of the defaults (but stay in the picker) and
  // the drawer's prefs never bleed into the main /assets table.
  const columnOptions = deriveColumnOptions(excludeDefaultColumns);
  const {
    columns,
    activeColumns,
    setActiveColumns,
    placeholder,
    sortableColumnsConfig,
  } = useAssetColumns(columnOptions);

  // Fetch capabilities for consistent integration display names
  const { data: capabilitiesData } = useGetCapabilities();

  // Fetch origin statistics for export functionality
  const { data: sourceStatistics, status: sourceStatisticsStatus } = useMy({
    resource: 'statistic',
    query: 'asset#origin',
  });

  // Build source options for export dropdown
  // Uses backend capabilities data to ensure consistency with table columns
  const sourceOptions = useMemo<SourceOption[]>(
    () =>
      buildSourceOptions(sourceStatistics, capabilitiesData.allCapabilities),
    [sourceStatistics, capabilitiesData.allCapabilities]
  );

  // Create dynamic export config with source options
  const assetExportConfig = useMemo(
    () => createAssetExportConfig(sourceOptions),
    [sourceOptions]
  );

  // Compute export active filters for UniversalExportModal
  const exportActiveFilters = useMemo(
    () =>
      buildActiveFilters(
        filters.advancedFilters as Record<string, unknown> | undefined,
        (assetFilterOptions as Record<
          string,
          (FilterOption | GroupOption)[]
        >) ?? {},
        filters.search,
        assetFieldLabels
      ),
    [
      filters.search,
      filters.advancedFilters,
      assetFilterOptions,
      assetFieldLabels,
    ]
  );

  // Handle Get Started method selection
  const handleGetStartedMethodSelect = useCallback(
    (methodId: AssetGetStartedMethodId) => {
      setIsGetStartedModalOpen(false);

      switch (methodId) {
        case 'integrations': {
          // Build URL with drawer state params for atomic navigation + drawer open
          const drawerParams = new URLSearchParams();
          drawerParams.set('drawerCompositeKey', 'integration');
          drawerParams.set('drawerOrder', JSON.stringify(['integration']));

          navigate(
            `../${AuthenticatedRoute.INTEGRATIONS}?${drawerParams.toString()}`,
            { relative: 'path' }
          );
          break;
        }
        case 'seeds':
          // Open AddSeedByTypeModal directly instead of navigating
          setIsAddSeedModalOpen(true);
          break;
        case 'import':
          setIsImportModalOpen(true);
          break;
      }
    },
    [
      navigate,
      setIsGetStartedModalOpen,
      setIsImportModalOpen,
      setIsAddSeedModalOpen,
    ]
  );

  return (
    <ConditionalRender
      condition={!hideTitle}
      conditionalWrapper={children => (
        <AppRouteBody title="Assets">{children}</AppRouteBody>
      )}
    >
      <FeatureLock locked={false}>
        {isInitialLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader isLoading type="spinner" />
          </div>
        ) : (
          <>
            {!isAllEmpty && (
              <TableControlsContainer
                data-testid="assets-controls"
                filterLabel={!hideTitle}
                filters={
                  <AssetTableFilters
                    filters={filters}
                    setFilters={setFilters}
                    visibleFilters={drawerFilters}
                  />
                }
                filtersRight={
                  <SearchBar
                    size="sm"
                    className="h-fit w-64"
                    placeholder="Search"
                    value={filters.search}
                    onChange={value =>
                      setFilters(prev => ({
                        ...prev,
                        search: value,
                      }))
                    }
                  />
                }
                selectionBar={
                  <>
                    <TableSelectionBar
                      embedded={true}
                      alwaysVisible={true}
                      isVisible={isRowsSelected}
                      selectedCount={selectedCount}
                      totalCount={
                        filteredCount ?? totalDatabaseCount ?? assets.length
                      }
                      hasNextPage={assetsHasNextPage}
                      isSelectAll={isSelectAll}
                      resourceLabel="assets"
                      emptySelectionMessage={
                        isFilteredCountLoading
                          ? 'Counting assets\u2026'
                          : filteredCount !== undefined
                            ? `${filteredCount.toLocaleString()} assets`
                            : `${totalDatabaseCount.toLocaleString()} assets`
                      }
                      actions={
                        !hideBulkActions ? (
                          <AssetActions
                            assets={selectedAssets}
                            onCommentClick={() => setIsCommentModalOpen(true)}
                            onTagClick={() => setIsTagModalOpen(true)}
                            onRemoveTagClick={() =>
                              setIsRemoveTagModalOpen(true)
                            }
                            onImportClick={() => setIsImportModalOpen(true)}
                            onExportClick={() => setIsExportModalOpen(true)}
                            hideActions={false}
                            disabled={selectedCount === 0}
                            selectionState={selectionState}
                            onRemoveProfileBulk={() =>
                              removeProfileConfirm.openConfirmation({
                                action: 'remove_profile',
                                actionName: 'remove scan profile',
                                // For mode 'all' (Select All Matching), the BE handler snapshots the
                                // unique profiles matched by the query at enqueue time and attaches them
                                // to params.profile_ids transparently. For mode 'manual' (explicit
                                // selection), the FE provides profile_ids from the loaded selectedAssets
                                // since it has that snapshot itself. Both flows close the race window
                                // where a user could remove a profile and quickly assign a new one
                                // before the queued worker runs.
                                params:
                                  selectionState.mode === 'all'
                                    ? {}
                                    : {
                                        profile_ids: Array.from(
                                          new Set(
                                            selectedAssets
                                              .map(a => a.profile)
                                              .filter(Boolean)
                                          )
                                        ),
                                      },
                              })
                            }
                          />
                        ) : undefined
                      }
                    />
                    {!hideBulkActions && (
                      <SelectionBanner
                        selectedCount={selectedCount}
                        totalLoadedCount={assets.length}
                        hasNextPage={assetsHasNextPage}
                        isSelectAll={isSelectAll}
                        isAllLoadedSelected={isAllLoadedSelected}
                        resourceLabel="assets"
                        onSelectAllMatching={selectAllMatching}
                        onClearSelection={clearSelection}
                        enableSelectAllMatching={activeQuery != null}
                      />
                    )}
                  </>
                }
              />
            )}
            {/* Single table-scoped aria-live region for bulk action status.
                UX divergence #6: one region per table, NOT one per row.
                Announces "N bulk actions in progress" for screen readers. */}
            <BulkActivityAnnouncer />
            <AssetTable
              key={combinedTableKey}
              name={name}
              assets={assets}
              assetsStatus={assetsStatus}
              assetsError={assetsError}
              fetchNextPage={fetchNextPage}
              isFetching={isFetching}
              isFetchingNextPage={isFetchingNextPage}
              assetsHasNextPage={assetsHasNextPage}
              isAllEmpty={isAllEmpty}
              isQueryError={isQueryError}
              columns={columns}
              activeColumns={activeColumns}
              setActiveColumns={setActiveColumns}
              placeholder={placeholder}
              sortableColumnsConfig={sortableColumnsConfig}
              selectedRows={selectedKeys}
              setSelectedRows={setSelectedKeys}
              filters={filters}
              setFilters={setFilters}
              handlers={handlers}
              handleKeyboardAction={handleKeyboardAction}
              setIsCommentModalOpen={setIsCommentModalOpen}
              setIsTagModalOpen={setIsTagModalOpen}
              navigate={navigate}
              resetQueryParams={resetQueryParams}
              drawerState={drawerState}
              onGetStartedClick={
                canWriteAssets
                  ? () => setIsGetStartedModalOpen(true)
                  : undefined
              }
              // Always render full-width with horizontal scroll (the former
              // drawer-only behavior). The responsive column-hiding engine is
              // disabled in every context now, so the main /assets table and the
              // embedded affected-assets table behave identically.
              responsive={false}
              hideBulkActions={hideBulkActions}
              onRowRemoveProfile={(assetKey: string) => {
                const profile = assets.find(a => a.key === assetKey)?.profile;
                removeProfileRowMutation.mutate({
                  action: 'remove_profile',
                  params: profile ? { profile_ids: [profile] } : {},
                  selection: { mode: 'explicit', keys: [assetKey] },
                });
              }}
            />
            <NoteModal
              open={isCommentModalOpen}
              onClose={() => setIsCommentModalOpen(false)}
              onSubmit={handleComment}
              resourceType="Asset"
              mode="simple"
              selectedCount={selectedCount}
              presentation="modal"
            />
            <StatusConfirmationModal
              confirmStatus={confirmStatus}
              onClose={() => setConfirmStatus(null)}
              onConfirm={async (data, status) => {
                await handlers.handleUpdateStatus(
                  data.map(asset => ({ key: asset.key })),
                  status
                );
                invalidateAssets();
                clearSelection();
                setConfirmStatus(null);
              }}
              isPraetorianUser={isPraetorianUser}
            />
            <BulkConfirmationDialog
              isOpen={statusConfirm.confirmPayload !== null}
              actionName={statusConfirm.confirmPayload?.actionName ?? ''}
              resourceLabel="assets"
              isSelectAll={selectionState.mode === 'all'}
              itemCount={selectedCount}
              isPending={statusConfirm.isPending}
              onCancel={statusConfirm.closeConfirmation}
              onConfirm={statusConfirm.submitConfirmed}
            />
            <TagModal
              assetKeys={selectedKeys}
              currentTags={[]}
              open={isTagModalOpen}
              onClose={() => setIsTagModalOpen(false)}
              onSuccess={clearSelection}
            />
            <RemoveTagModal
              assetKeys={selectedKeys}
              open={isRemoveTagModalOpen}
              onClose={() => setIsRemoveTagModalOpen(false)}
              onSuccess={clearSelection}
              selectionState={selectionState}
              onBulkRequested={tag =>
                untagConfirm.openConfirmation({
                  action: 'untag',
                  actionName: 'untag',
                  params: { tag },
                })
              }
            />
            <BulkConfirmationDialog
              isOpen={untagConfirm.confirmPayload !== null}
              actionName={untagConfirm.confirmPayload?.actionName ?? ''}
              resourceLabel="assets"
              isSelectAll={selectionState.mode === 'all'}
              itemCount={selectedCount}
              isPending={untagConfirm.isPending}
              onCancel={untagConfirm.closeConfirmation}
              onConfirm={untagConfirm.submitConfirmed}
            />
            <BulkConfirmationDialog
              isOpen={removeProfileConfirm.confirmPayload !== null}
              actionName={removeProfileConfirm.confirmPayload?.actionName ?? ''}
              resourceLabel="assets"
              isSelectAll={selectionState.mode === 'all'}
              itemCount={selectedCount}
              isPending={removeProfileConfirm.isPending}
              onCancel={removeProfileConfirm.closeConfirmation}
              onConfirm={removeProfileConfirm.submitConfirmed}
            />
            <ImportAssetsModal
              open={isImportModalOpen}
              onClose={() => setIsImportModalOpen(false)}
              onImport={() => {
                invalidateAssets();
                setIsImportModalOpen(false);
              }}
            />
            {sourceStatisticsStatus === 'success' && (
              <UniversalExportModal
                config={assetExportConfig}
                open={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                selectedRows={isSelectAll ? [] : selectedKeys}
                onClearSelection={() => {
                  clearSelection();
                  setIsExportModalOpen(false);
                }}
                activeFilters={exportActiveFilters}
                activeQuery={
                  (activeQuery ?? undefined) as GraphQuery | undefined
                }
                totalMatchingCount={filteredCount}
              />
            )}
            <GetStartedModal
              open={isGetStartedModalOpen}
              onClose={() => setIsGetStartedModalOpen(false)}
              onSelectMethod={handleGetStartedMethodSelect}
              methods={ASSET_GET_STARTED_METHODS}
            />
            <AddSeedByTypeModal
              open={isAddSeedModalOpen}
              onClose={() => setIsAddSeedModalOpen(false)}
              onSuccess={() => {
                invalidateAssets();
                setIsAddSeedModalOpen(false);
              }}
            />
          </>
        )}
      </FeatureLock>
    </ConditionalRender>
  );
}
